<script setup lang="ts">
/**
 * StructureEditView – Takt-Editor „Wiederholungen & Taktstriche" im NoteActionsOverlay.
 *
 * Bearbeitet Wiederholungen (`|:`/`:|` inkl. Durchgangs-Anzahl), Volta-Klammern (1./2.) und den
 * Stil des rechten Taktstrichs. Takt einfügen/löschen ist ein EIGENER Reiter
 * (`MeasuresEditView`). Der Zustand kommt reaktiv aus
 * `currentMeasureStructure` (aus dem XML abgeleitet – bleibt nach Commit/Undo frisch); die
 * Toggles wirken sofort (kein invalider Zwischenzustand, anders als bei der Taktart).
 * `structureSide` (vom Taktkanten-Doppelklick) akzentuiert die linke bzw. rechte Sektion rein
 * visuell – kein Fokus (Regel 9a), kein Scrollen.
 */
import {computed, ref, watch} from 'vue';
import {Icon} from '@iconify/vue';
import type {BarStyleChoice, EndingSpan} from '@/composables/musicXmlDom';
import {injectScoreEditContext} from '@/components/musicxml-preview/editContext.ts';

const {
  currentMeasureStructure,
  structureSide,
  selectedMeasureIndex: measureIndex,
  selectedMeasure: measureNumber,
  selectedNotes,
  setRepeat,
  setRepeatTimes,
  setBarStyle,
  setEnding,
  removeEnding,
} = injectScoreEditContext();

const info = currentMeasureStructure;

// ---- Wiederholung ---------------------------------------------------------------------------

function toggleRepeat(side: 'start' | 'end'): void {
  const current = side === 'start' ? info.value?.repeatStart : info.value?.repeatEnd;
  setRepeat(side, !current);
}

/** Durchgangs-Anzahl als String (leeres Feld = Attribut entfernen = Default 2 Durchgänge). */
const timesDraft = ref('');
watch(() => info.value?.repeatTimes, (times) => {
  timesDraft.value = times === null || times === undefined ? '' : String(times);
}, {immediate: true});

function commitTimes(): void {
  const parsed = parseInt(timesDraft.value, 10);
  setRepeatTimes(Number.isInteger(parsed) && parsed >= 2 ? Math.min(parsed, 9) : null);
}

// ---- Taktstrich (rechts) --------------------------------------------------------------------

const BAR_STYLES: { value: BarStyleChoice; label: string }[] = [
  {value: 'regular', label: 'Normal'},
  {value: 'light-light', label: 'Doppelstrich'},
  {value: 'light-heavy', label: 'Schlussstrich'},
];

// ---- Volta-Klammer --------------------------------------------------------------------------

const endingNumberDraft = ref(1);
// Bereich als 1-basierte Takt-POSITION (Ordinalindex + 1; bei Auftakt kann die gedruckte Nummer
// abweichen – die Markierung im Notenbild zeigt den getroffenen Takt).
const endingFromDraft = ref(1);
const endingToDraft = ref(1);

/** Vorbelegung: Mehrtakt-Auswahl (min/max der ausgewählten Takte), sonst der aktive Takt. */
watch([() => measureIndex.value, () => info.value?.ending ?? null], ([idx]) => {
  if (idx === null) return;
  const existing = info.value?.ending;
  if (existing) {
    endingNumberDraft.value = existing.number;
    endingFromDraft.value = existing.startIndex + 1;
    endingToDraft.value = existing.endIndex + 1;
    return;
  }
  const selectedIndices = [...new Set(
      selectedNotes.value.map(n => n.measureIndex).filter((i): i is number => i !== undefined),
  )];
  const from = selectedIndices.length ? Math.min(...selectedIndices) : idx;
  const to = selectedIndices.length ? Math.max(...selectedIndices) : idx;
  endingFromDraft.value = from + 1;
  endingToDraft.value = to + 1;
}, {immediate: true});

const measureCount = computed(() => info.value?.measureCount ?? 0);

const endingRange = computed((oldValue?: { from: number; to: number }) => {
  const from = Math.min(endingFromDraft.value, endingToDraft.value) - 1;
  const to = Math.max(endingFromDraft.value, endingToDraft.value) - 1;
  // oldValue-Muster (Pflicht für objekt-liefernde Computeds, VUE_RULES Regel 2).
  if (oldValue && oldValue.from === from && oldValue.to === to) return oldValue;
  return {from, to};
});

const endingRangeValid = computed(() =>
    Number.isInteger(endingRange.value.from) && Number.isInteger(endingRange.value.to)
    && endingRange.value.from >= 0 && endingRange.value.to < measureCount.value,
);

/** Klammern, die der gewählte Bereich ersetzen würde (der Writer ersetzt strukturell – die
 *  Warnung erklärt es nur). */
const replacedEndings = computed((oldValue?: EndingSpan[]) => {
  const next = (info.value?.endings ?? []).filter(s =>
      s.startIndex <= endingRange.value.to && s.endIndex >= endingRange.value.from);
  // oldValue-Muster: gleiche Spans -> gleiche Referenz (Signatur-Vergleich).
  if (oldValue && oldValue.length === next.length
      && oldValue.every((s, i) => s.number === next[i]!.number
          && s.startIndex === next[i]!.startIndex && s.endIndex === next[i]!.endIndex)) {
    return oldValue;
  }
  return next;
});

function applyEnding(): void {
  if (!endingRangeValid.value) return;
  setEnding(endingNumberDraft.value, endingRange.value.from, endingRange.value.to);
}

// ---- Anzeige-Helfer -------------------------------------------------------------------------

/** Akzent der vom Kanten-Doppelklick getroffenen Seite (links = |:, rechts = :| + Taktstrich). */
const accentLeft = computed(() => structureSide.value === 'left');
const accentRight = computed(() => structureSide.value === 'right');

const TOGGLE_CLASS = 'flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors';
const SECTION_ACCENT = 'ring-2 ring-teal-300 rounded-xl';
</script>

<template>
  <Transition
      enter-active-class="animate-fade animate-duration-200 animate-ease-out"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
  >
    <div
        v-if="measureIndex !== null && info"
        class="flex items-center justify-center p-2 overflow-auto"
    >
      <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden ring-1 ring-black/5">

        <!-- Header -->
        <div class="flex items-center justify-between p-5 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-emerald-50">
          <div>
            <h2 class="text-xl font-bold text-gray-800 flex items-center gap-3">
              <div class="p-2 bg-teal-100 text-teal-600 rounded-lg">
                <Icon icon="fa6-solid:repeat" class="text-lg"/>
              </div>
              Wiederholungen &amp; Taktstriche
            </h2>
            <p class="text-sm text-gray-600 mt-1">Takt {{ measureNumber }} (Position {{ measureIndex + 1 }} von {{ measureCount }})</p>
          </div>
        </div>

        <!-- Content -->
        <div class="p-5 space-y-5">

          <!-- Wiederholung -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Wiederholung</label>
            <div class="flex gap-2">
              <button
                  type="button"
                  :class="[TOGGLE_CLASS, accentLeft ? SECTION_ACCENT : '', info.repeatStart
                    ? 'bg-teal-500 border-teal-500 text-white'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50']"
                  title="Wiederholung beginnt hier (𝄆)"
                  @click="toggleRepeat('start')"
              >
                𝄆 beginnt hier
              </button>
              <button
                  type="button"
                  :class="[TOGGLE_CLASS, accentRight ? SECTION_ACCENT : '', info.repeatEnd
                    ? 'bg-teal-500 border-teal-500 text-white'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50']"
                  title="Wiederholung endet hier (𝄇)"
                  @click="toggleRepeat('end')"
              >
                𝄇 endet hier
              </button>
            </div>
            <div v-if="info.repeatEnd" class="flex items-center gap-2 text-sm text-gray-700">
              <label for="repeat-times" class="font-medium">Anzahl Durchgänge</label>
              <input
                  id="repeat-times"
                  v-model="timesDraft"
                  type="number"
                  min="2"
                  max="9"
                  placeholder="2"
                  class="w-16 rounded-lg border border-gray-200 px-2 py-1 text-center"
                  @change="commitTimes"
              />
              <span class="text-gray-400">(leer = 2)</span>
            </div>
          </div>

          <!-- Volta-Klammer -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Klammer (Volta 1./2.)</label>

            <div v-if="info.ending" class="flex items-center justify-between gap-2 rounded-lg bg-teal-50 border border-teal-200 px-3 py-2 text-sm text-teal-800">
              <span>Klammer {{ info.ending.number }}: Takt-Position {{ info.ending.startIndex + 1 }}–{{ info.ending.endIndex + 1 }}</span>
              <button
                  type="button"
                  class="rounded-lg border border-red-200 bg-white px-2 py-1 text-red-600 hover:bg-red-50"
                  @click="removeEnding"
              >
                Entfernen
              </button>
            </div>

            <template v-else>
              <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                <div class="flex gap-1">
                  <button
                      v-for="n in [1, 2]"
                      :key="n"
                      type="button"
                      class="w-9 rounded-lg border py-1.5 font-mono font-semibold"
                      :class="endingNumberDraft === n
                        ? 'bg-teal-500 border-teal-500 text-white'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'"
                      @click="endingNumberDraft = n"
                  >
                    {{ n }}.
                  </button>
                </div>
                <label for="ending-from">von Position</label>
                <input
                    id="ending-from"
                    v-model.number="endingFromDraft"
                    type="number" :min="1" :max="measureCount"
                    class="w-16 rounded-lg border border-gray-200 px-2 py-1 text-center"
                />
                <label for="ending-to">bis</label>
                <input
                    id="ending-to"
                    v-model.number="endingToDraft"
                    type="number" :min="1" :max="measureCount"
                    class="w-16 rounded-lg border border-gray-200 px-2 py-1 text-center"
                />
                <button
                    type="button"
                    class="rounded-lg bg-teal-500 px-3 py-1.5 font-semibold text-white hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-50"
                    :disabled="!endingRangeValid"
                    @click="applyEnding"
                >
                  Setzen
                </button>
              </div>
              <div
                  v-if="replacedEndings.length"
                  class="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-700"
              >
                <Icon icon="fa6-solid:triangle-exclamation" class="mt-0.5 shrink-0"/>
                <span>
                  Ersetzt
                  <template v-for="(s, i) in replacedEndings" :key="s.startIndex">{{ i ? ', ' : '' }}Klammer {{ s.number }} (Position {{ s.startIndex + 1 }}–{{ s.endIndex + 1 }})</template>.
                </span>
              </div>
            </template>
          </div>

          <!-- Taktstrich (rechts) – ausgeblendet, solange die Wiederholung ihn zeichnet. -->
          <div v-if="!info.repeatEnd" class="space-y-2" :class="accentRight ? SECTION_ACCENT + ' p-2' : ''">
            <label class="block text-sm font-medium text-gray-700">Taktstrich (rechts)</label>
            <div class="flex gap-2">
              <button
                  v-for="style in BAR_STYLES"
                  :key="style.value"
                  type="button"
                  :class="[TOGGLE_CLASS, info.barStyle === style.value
                    ? 'bg-teal-500 border-teal-500 text-white'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50']"
                  @click="setBarStyle(style.value)"
              >
                {{ style.label }}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  </Transition>
</template>
